import { useMemo } from 'react'

import { useForm } from 'react-hook-form'
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from '@chakra-ui/react'

import { describeTeamCreationBillingImpact } from '@/helpers/team-creation-billing/team-creation-billing'
import { generateTeamName } from '@/helpers/team-name/team-name'
import {
  useCreateTeam,
  useTeamCreationBillingPreview,
} from '@/react-query/team'
import { useUser } from '@/react-query/user'
import { PlusIcon } from '@/views/assets/PlusIcon'

import { useCreateTeamAndCheckout } from './useCreateTeamAndCheckout'

export const CreateTeam = () => {
  const { onOpen, isOpen, onClose } = useDisclosure()

  return (
    <Box mt={10}>
      <Button
        variant={'brand-solid'}
        leftIcon={<PlusIcon stroke={'white'} />}
        onClick={onOpen}
      >
        Create a new team
      </Button>
      <CreateTeamModal isOpen={isOpen} onClose={onClose} />
    </Box>
  )
}

interface CreateTeamForm {
  name: string
}

const CreateTeamModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: () => void
}) => {
  const createTeam = useCreateTeam()
  const createTeamAndCheckout = useCreateTeamAndCheckout()

  const { data: user } = useUser()
  const paidPersonalPlan =
    user?.plan === 'PRO' || user?.plan === 'BUSINESS' ? user.plan : null
  const hasPaidPersonalPlan = paidPersonalPlan !== null
  const billingPreview = useTeamCreationBillingPreview(
    isOpen && hasPaidPersonalPlan,
  )
  // Without the preview the dialog still warns, just without amounts.
  const personalPlan =
    billingPreview.data ??
    (billingPreview.isError && paidPersonalPlan
      ? {
          plan: paidPersonalPlan,
          billingPeriod: null,
          price: null,
          currency: null,
          lookupKey: null,
          unusedCredit: null,
        }
      : null)

  const defaultValues = useMemo(() => {
    return {
      name: generateTeamName(user?.email || '', user?.displayName || ''),
    }
  }, [user])

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    register,
    reset,
  } = useForm<CreateTeamForm>({
    defaultValues,
  })

  const onSubmit = async (data: CreateTeamForm) => {
    if (!hasPaidPersonalPlan) {
      await createTeam.mutateAsync(data)
      onClose()
      return
    }
    await createTeamAndCheckout(data, personalPlan?.lookupKey ?? null)
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create a new team</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <form>
            <FormControl mb={4}>
              <FormLabel htmlFor="name">Team name:</FormLabel>
              <Input
                {...register('name', { required: true })}
                type="text"
                id="name"
                placeholder="Enter team name"
                variant={'brand-solid-white'}
                required={true}
              />
              {errors.name && <span>This field is required</span>}
            </FormControl>
          </form>
          {personalPlan && (
            <Alert status="warning" borderRadius="md" alignItems="flex-start">
              <AlertIcon />
              {describeTeamCreationBillingImpact(personalPlan)}
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            variant="brand-solid"
            type="submit"
            onClick={handleSubmit(onSubmit)}
            isLoading={isSubmitting}
            isDisabled={hasPaidPersonalPlan && billingPreview.isLoading}
          >
            {hasPaidPersonalPlan
              ? 'Create team & continue to billing'
              : 'Create a team'}
          </Button>
          <Button
            variant="brand-solid-white"
            onClick={() => {
              reset()
              onClose()
            }}
          >
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
